import Dropzone from 'dropzone'
import template from 'lodash.template'
import { useDispatch } from 'stimulus-use'
import { Controller as BaseController } from 'stimulus'
import Rails from '@rails/ujs'
import I18n from '@app/javascript/i18n/index.js.erb'
import './index.scss'
import { DirectUpload } from '@rails/activestorage'
import 'custom-event-polyfill'

export class Controller extends BaseController {
  static targets = [
    'input',
    'selectionsList',
    'selectionsListAnnouncement',
    'selectionPillTemplate',
    'text',
    'error',
    'uploadPill'
  ]

  static values = {
    maxFiles: Number,
    maxFilesize: Number,
    acceptedFiles: String,
    freeSpace: Number,
    directUploadUrl: String,
    fileValidationUrl: String,
    rollbackUrl: String
  }

  static classes = ['uploadPill', 'pillBackground', 'pillErrorLeft', 'pillErrorRight']

  errors = {}
  files = {}

  connect() {
    useDispatch(this)
    this.dropZone = this.createDropZone()
    this.hideFileInput()
    this.selectionPillTemplate = template(this.selectionPillTemplateTarget.innerHTML)
    this.bindEvents()
  }

  createDropZone() {
    return new Dropzone(this.element.children.dropzone, {
      url: '#',
      headers: this.headers,
      maxFiles: this.maxFilesValue,
      maxFilesize: this.maxFilesizeValue / 1024 / 1024,
      acceptedFiles: this.acceptedFilesValue,
      disablePreviews: true,
      autoProcessQueue: false,
      clickable: '.dz-clickable'
    })
  }

  hideFileInput() {
    this.inputTarget.style.display = 'none'
  }

  clickButton() {
    this.inputTarget.click()
  }

  bindEvents() {
    document.getElementById('entity_file').addEventListener('change', (event) => {
      const file = event.target.files[0]
      file.upload = { uuid: Dropzone.uuidv4() }

      this.readFile(file)

      event.target.value = null
    })

    this.dropZone.on('addedfile', file => {
      this.readFile(file)
    })
  }

  readFile(file) {
    this.dispatch('addFile', file)

    const reader = new FileReader()
    reader.addEventListener('loadend', (event) => {
      file.dataURL = event.target.result
      this.validateFile(file, (valid) => {
        this.createFile(file, valid)
        this.freeSpaceValue -= file.size
        this.files[file.upload.uuid] = file
        this.updateErrorState()
      })
    })
    reader.readAsDataURL(file)
  }

  submitForm(_e) {
    const context = this
    const filesArray = Object.values(this.files)
    const apiPromises = filesArray.map(file => {
      return new Promise(function(resolve, reject) {
        createDirectUploadController(context, file)
          .start()
          .then(result => resolve(result))
          .catch(error => reject(error))
      })
    })

    Promise.all(apiPromises).then(result => {
      const ids = result.map(item => item.user_document_id)
      this.dispatch('directUploadDone', { files_ids: ids })
    }).catch(() => {
      if (this.rollbackUrlValue) {
        this.rollback(filesArray.map(file => file.name))
      }
    })
  }

  rollback(filesNames) {
    Rails.ajax({
      type: 'DELETE',
      url: this.rollbackUrlValue,
      data: $.param({
        files_names: filesNames
      }),
      success: () => {
        this.dispatch('directUploadFailed')
      }
    })
  }

  removeFile(e) {
    delete this.errors[e.currentTarget.dataset.fileUuid]
    delete this.files[e.currentTarget.dataset.fileUuid]

    this.freeSpaceValue += Number(e.currentTarget.dataset.fileSize)

    this.updateErrorState()
  }

  updateErrorState() {
    const textMessages = []
    const errorMessages = [...new Set(Object.values(this.errors))]

    if (this.freeSpaceValue < 0) {
      errorMessages.push(I18n.t('drag_and_drop.errors.space_limit'))
    }

    if (Object.keys(this.files).length > 0) {
      textMessages.push(I18n.t('drag_and_drop.upload_message'))
    }

    if (Object.keys(this.files).length > this.maxFilesValue) {
      errorMessages.push(I18n.t('drag_and_drop.errors.file_count', { max_count: this.maxFilesValue }))
    }

    this.textTarget.innerHTML = textMessages.join('<br/>')
    this.errorTarget.innerHTML = errorMessages.join('<br/>')

    if (errorMessages.length) {
      this.dispatch('errors')
    } else {
      this.dispatch('clean')
    }
  }

  validateFile(file, onValidated) {
    Rails.ajax({
      type: 'POST',
      url: this.fileValidationUrlValue,
      data: $.param({
        filename: file.name
      }),
      success: (resp) => {
        const filenameTaken = !resp.valid_filename || Object.values(this.files).find(
          f => this.sansExtension(f.name) === this.sansExtension(file.name)
        )
        const invalidMimetype = !this.acceptedFilesValue.includes(file.type)

        if (file.size > this.maxFilesizeValue) {
          this.errors[file.upload.uuid] = I18n.t('drag_and_drop.errors.file_size', { limit: `${this.maxFilesizeValue / 1024 / 1024} MB` })
          return onValidated(false)
        }

        if (filenameTaken) {
          this.errors[file.upload.uuid] = I18n.t('drag_and_drop.errors.filename')
          return onValidated(false)
        }

        if (invalidMimetype) {
          this.errors[file.upload.uuid] = I18n.t('drag_and_drop.errors.mimetype')
          return onValidated(false)
        }

        return onValidated(true)
      }
    })
  }

  createFile(file, valid = true) {
    const name = this.selectionsListTarget.dataset.inputName

    const pill = $(this.selectionPillTemplate({
      index: this.selectionsListTarget.children.length + 1,
      text: file.name,
      value: file.name,
      name
    }))

    pill.addClass(this.uploadPillClass)
    pill.children('button').attr('data-file-uuid', file.upload.uuid)
    pill.children('button').attr('data-file-size', file.size)

    if (valid) {
      pill.children('div').addClass(this.pillBackgroundClass)
    } else {
      pill.children('div').addClass(this.pillErrorLeftClass)
      pill.children('button').addClass(this.pillErrorRightClass)
    }

    $(this.selectionsListTarget).append(pill)
  }

  get headers() {
    return { 'X-CSRF-Token': this.getMetaValue('csrf-token') }
  }

  getMetaValue(name) {
    const element = document.head.querySelector(`meta[name="${name}"]`)
    if (element) {
      return element.getAttribute('content')
    }
  }

  sansExtension(name) {
    return name.split('.').slice(0, -1).join('.')
  }

  get url() {
    return this.directUploadUrlValue
  }
}

class DirectUploadController {
  constructor(source, file) {
    this.directUpload = createDirectUpload(file, source.url, this)
    this.source = source
    this.file = file
  }

  start() {
    this.file.controller = this
    this.hiddenInput = this.createHiddenInput()
    const controller = this

    return new Promise(function(resolve, reject) {
      controller.directUpload.create((error, attributes) => {
        if (error) {
          removeElement(controller.hiddenInput)
          reject(error)
        } else {
          controller.hiddenInput.value = attributes.signed_id
          resolve(attributes)
        }
      })
    })
  }

  createHiddenInput() {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = this.source.inputTarget.name
    insertAfter(input, this.source.inputTarget)
    return input
  }
}

export function getMetaValue(name) {
  const element = findElement(document.head, `meta[name="${name}"]`)
  if (element) {
    return element.getAttribute('content')
  }
}

export function findElement(root, selector) {
  if (typeof root === 'string') {
    selector = root
    root = document
  }
  return root.querySelector(selector)
}

export function removeElement(el) {
  el?.parentNode?.removeChild(el)
}

export function insertAfter(el, referenceNode) {
  return referenceNode.parentNode.insertBefore(el, referenceNode.nextSibling)
}

// Top level...
function createDirectUploadController(source, file) {
  return new DirectUploadController(source, file)
}

function createDirectUpload(file, url, controller) {
  return new DirectUpload(file, url, controller)
}
